import classNames from 'classnames';
import {isEmpty, times} from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import Split from 'split.js';

import styles from './SplitLayout.less';
import {LIBRARY_NAME} from 'src/js/common/constants';
import {LogUtils} from 'src/js/common/utils';

const SPLIT_LAYOUT_H = 'horizontal';
const SPLIT_LAYOUT_V = 'vertical';
const LOCAL_STORAGE_ID_PREFIX = 'SplitLayout';

const logger = LogUtils.getLogger('SplitLayout', LIBRARY_NAME);

class SplitLayout extends React.PureComponent {
  constructor(props) {
    super(props);
    const {children} = this.props;
    // React.Children.count() counts also null children, filter them out
    const nonEmptyChildCount = React.Children.toArray(children).filter(
      (child) => React.isValidElement(child) && !isEmpty(child)
    ).length;
    // create refs for each child
    this.splitPanelRefs = times(nonEmptyChildCount, React.createRef);

    this.loadState = this.loadState.bind(this);
    this.saveState = this.saveState.bind(this);
  }

  componentDidMount() {
    const {children, layoutId, useFlexBox, ...splitOptions} = this.props;
    if (!isEmpty(this.splitPanelRefs)) {
      const currentPanels = this.splitPanelRefs.map((panelRef) => {
        if (isEmpty(panelRef.current)) {
          logger.warn('Split panel node ref is empty!');
        }
        return panelRef.current;
      });
      this.splitInstance = Split(currentPanels, {
        ...splitOptions,
        ...this.loadState(),
        ...(useFlexBox
          ? {
              elementStyle: (dimension, size, gutterSize) => ({
                'flex-basis': `calc(${size}% - ${gutterSize}px)`,
                ...(splitOptions.elementStyle
                  ? splitOptions.elementStyle(dimension, size, gutterSize)
                  : {}),
              }),
              gutterStyle: (dimension, gutterSize) => ({
                'flex-basis': `${gutterSize}px`,
                ...(splitOptions.gutterStyle
                  ? splitOptions.gutterStyle(dimension, gutterSize)
                  : {}),
              }),
            }
          : {}),
        onDragEnd: (event) => {
          this.saveState();
          if (splitOptions.onDragEnd) {
            splitOptions.onDragEnd(event);
          }
        },
      });
    }
  }

  componentWillUnmount() {
    if (this.splitInstance) {
      this.splitInstance.destroy();
    }
  }

  getLocalStorageId() {
    const {layoutId} = this.props;
    return layoutId ? `${LOCAL_STORAGE_ID_PREFIX}:${layoutId}` : undefined;
  }

  loadState() {
    const loadedState = {};
    const localStorageId = this.getLocalStorageId();
    if (localStorageId) {
      const sizes = localStorage.getItem(localStorageId);
      if (sizes) {
        loadedState.sizes = JSON.parse(sizes);
      }
    }
    return loadedState;
  }

  saveState() {
    const localStorageId = this.getLocalStorageId();
    if (this.splitInstance && localStorageId) {
      localStorage.setItem(
        localStorageId,
        JSON.stringify(this.splitInstance.getSizes())
      );
    }
  }

  render() {
    const {children, direction, useFlexBox} = this.props;
    // React.Children.count() counts also null children, filter them out
    const nonEmptyChildCount = React.Children.toArray(children).filter(
      (child) => React.isValidElement(child) && !isEmpty(child)
    ).length;
    let childIdx = 0;

    return (
      <div
        className={classNames({
          [styles.dlFlexContainer]: useFlexBox,
          [styles.dlFlexDirectionColumn]:
            useFlexBox && direction === SPLIT_LAYOUT_V,
          [styles.dlFlexDirectionRow]:
            useFlexBox && direction === SPLIT_LAYOUT_H,
        })}
      >
        {!isEmpty(this.splitPanelRefs) &&
        this.splitPanelRefs.length === nonEmptyChildCount
          ? React.Children.map(children, (child) => {
              const isNonEmpty = React.isValidElement(child) && !isEmpty(child);
              childIdx += isNonEmpty ? 1 : 0;
              return isNonEmpty
                ? React.cloneElement(child, {
                    ref: this.splitPanelRefs[childIdx - 1],
                    className: classNames(child.props.className, {
                      [styles.dlSplitPanel]: true,
                      [styles.dlSplitHorizontal]: direction === SPLIT_LAYOUT_H,
                    }),
                  })
                : null;
            })
          : children}
      </div>
    );
  }
}

SplitLayout.displayName = 'SplitLayout';

SplitLayout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  layoutId: PropTypes.string,
  useFlexBox: PropTypes.bool,
  // ////////////////
  // Split.js options
  // ////////////////
  // Initial sizes of each element in percents.
  sizes: PropTypes.arrayOf(PropTypes.any),
  // Minimum size of each element.
  minSize: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.number,
  ]),
  // Gutter size in pixels.
  gutterSize: PropTypes.number,
  // Snap to minimum size offset in pixels.
  snapOffset: PropTypes.number,
  // Direction to split: horizontal or vertical.
  direction: PropTypes.string,
  // Cursor to display while dragging.
  cursor: PropTypes.string,
  // Called to create each gutter element.
  gutter: PropTypes.func,
  // Called to set the style of each element.
  elementStyle: PropTypes.func,
  // Called to set the style of the gutter.
  gutterStyle: PropTypes.func,
  // Callback on drag.
  onDrag: PropTypes.func,
  // Callback on drag start.
  onDragStart: PropTypes.func,
  // Callback on drag end.
  onDragEnd: PropTypes.func,
};

SplitLayout.defaultProps = {
  layoutId: undefined,
  useFlexBox: false,
  sizes: undefined,
  minSize: 100,
  gutterSize: 10,
  snapOffset: 30,
  direction: SPLIT_LAYOUT_H,
  cursor: 'default',
  gutter: undefined,
  elementStyle: undefined,
  gutterStyle: undefined,
  onDrag: undefined,
  onDragStart: undefined,
  onDragEnd: undefined,
};

export default SplitLayout;
